import { Article } from '../../components/Layout'
import { H1, H2, H3, P, Lead, UL, LI, Card, Grid, Callout, Code, Divider } from '../../components/DocComponents'

const toc = [
  { id: 'what', title: 'What is Brainer?', level: 2 as const },
  { id: 'data-model', title: 'Data model layer', level: 2 as const },
  { id: 'workflow', title: 'Workflow layer', level: 2 as const },
  { id: 'plates', title: 'Plates layer', level: 2 as const },
  { id: 'terminology', title: 'Terminology map', level: 2 as const },
]

export function BrainerOverview() {
  return (
    <Article toc={toc}>
      <H1>Brainer Overview</H1>
      <Lead>Brainer is Rabbit's automation engine. It combines a flexible data-modelling system with a workflow execution engine and a dashboard builder — all in one service.</Lead>

      <H2 id="what">What is Brainer?</H2>
      <P>Think of Brainer as three stacked layers that build on each other:</P>
      <Grid cols={3}>
        <Card title="Data Model" icon="🗂️">Define entity types (Cups), their fields (Traits), and how they relate. Store records (Coffees) and their field values (Beans).</Card>
        <Card title="Workflows" icon="⚙️">Build multi-step Preparations. Collect data via interactive Interviewers, run automation Scripts (Drinkers), and track each run as a Journey.</Card>
        <Card title="Plates" icon="📊">Compose dashboard pages from Sections and Views. Display live records, charts, and forms. Publish publicly via OpenPlace.</Card>
      </Grid>
      <Callout type="info">Everything in Brainer is scoped to a <strong>Business</strong>. Data from different businesses is never mixed.</Callout>

      <H2 id="data-model">Data model layer</H2>
      <P>Brainer's data model is intentionally schema-flexible. You define the shape of your data without writing SQL or migrations:</P>
      <UL>
        <LI><strong>Ingredient</strong> — A primitive data type (text, number, date, boolean, UUID, …). The lowest-level building block.</LI>
        <LI><strong>Trait</strong> — A named field definition: an Ingredient plus validation rules, a display label, and an optional identifier flag. Traits are <em>reusable</em> across cups.</LI>
        <LI><strong>Collection</strong> — A named group of Traits, used as a template to add multiple traits to a cup at once.</LI>
        <LI><strong>Cup</strong> — An entity type (analogous to a database table). A Cup defines <em>which</em> Traits its records hold.</LI>
        <LI><strong>Cup Relation</strong> — A parent-child link between two Cups (analogous to a foreign key). Each relation has a label.</LI>
        <LI><strong>Coffee</strong> — A single record in a Cup.</LI>
        <LI><strong>Bean</strong> — The value of one Trait on one Coffee (one cell in the table).</LI>
        <LI><strong>Flavor</strong> — A link instance between two Coffees that have a Cup Relation.</LI>
      </UL>

      <H2 id="workflow">Workflow layer</H2>
      <UL>
        <LI><strong>Drinker</strong> — A JavaScript script that queries and transforms data. Returns a named result object.</LI>
        <LI><strong>Step</strong> — A unit of work in a Preparation. Steps have types: <em>Interviewer</em> (user Q&amp;A), <em>Generator</em> (automated), <em>Funnel</em> (conditional branch).</LI>
        <LI><strong>Preparation</strong> — An ordered sequence of Steps. The workflow <em>definition</em>.</LI>
        <LI><strong>Journey</strong> — One execution of a Preparation. Transitions through <Code>pending → running → paused → completed</Code>. A Journey is paused when waiting for user input.</LI>
        <LI><strong>Funnel / Connector</strong> — A conditional gate between Preparations. Routes a Journey to a different Preparation based on evaluated conditions.</LI>
      </UL>

      <H2 id="plates">Plates layer</H2>
      <UL>
        <LI><strong>Plate</strong> — A dashboard page. Has a lifecycle: <Code>draft → verified → deployed</Code>.</LI>
        <LI><strong>Plate Section</strong> — A column in the plate layout. Sections have a percentage width and an optional title.</LI>
        <LI><strong>Plate View</strong> — A widget inside a Section. Can display a list, table, chart, card, button, or an embedded Preparation Interviewer.</LI>
        <LI><strong>Input Parameter</strong> — A named parameter injected into all views when the plate runs. Can have a fixed value or be supplied by the user/URL.</LI>
        <LI><strong>OpenPlace</strong> — A published plate accessible without authentication at <Code>/open/:plateId</Code>.</LI>
      </UL>

      <Divider />

      <H2 id="terminology">Terminology map</H2>
      <P>If you're coming from a relational database background, this mapping may help:</P>
      <div className="overflow-x-auto rounded-lg border border-slate-200 mb-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Brainer term</th>
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">SQL analogy</th>
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Description</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {[
              ['Cup', 'Table', 'Entity type definition'],
              ['Cup Trait', 'Column', 'Field definition on a cup'],
              ['Coffee', 'Row', 'One record'],
              ['Bean', 'Cell', 'Value of one field on one record'],
              ['Cup Relation', 'Foreign key', 'Link between two cup types'],
              ['Flavor', 'Join row', 'One link instance between two records'],
              ['Trait', '(reusable column def)', 'Shareable field definition'],
            ].map(([b, s, d]) => (
              <tr key={b} className="hover:bg-slate-50">
                <td className="px-4 py-2 font-mono text-blue-700 text-[13px]">{b}</td>
                <td className="px-4 py-2 font-mono text-slate-500 text-[13px]">{s}</td>
                <td className="px-4 py-2 text-slate-600 text-[13px]">{d}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Article>
  )
}
